import React, { useState, useEffect } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { authAPI } from '../services/api';
import { ENABLE_GOOGLE_LOGIN, GOOGLE_CLIENT_ID } from '../config';
import packageJson from '../../package.json';
import './Login.css';

const TOKEN_KEY = 'deviceFleetToken';

export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

const features = [
  { icon: 'fa-mobile-alt',   text: 'Track all devices in real time'      },
  { icon: 'fa-users',        text: 'Assign & transfer across teams'       },
  { icon: 'fa-chart-bar',    text: 'Detailed usage statistics'            },
  { icon: 'fa-shield-alt',   text: 'Role-based access control'            },
];

function LoginForm({ onLoggedIn }) {
  const [error, setError]                     = useState('');
  const [loading, setLoading]                 = useState(false);
  const [serverGoogleEnabled, setServerGoogleEnabled] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authAPI.getConfig();
        if (!cancelled) setServerGoogleEnabled(!!data.googleLoginEnabled);
      } catch {
        if (!cancelled) setServerGoogleEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const canUseGoogle =
    ENABLE_GOOGLE_LOGIN &&
    !!GOOGLE_CLIENT_ID &&
    serverGoogleEnabled === true;

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);
    try {
      const { data } = await authAPI.googleLogin({
        credential: credentialResponse.credential
      });
      setStoredToken(data.token);
      onLoggedIn(data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lp-root">
      {/* ── Left brand panel ── */}
      <div className="lp-brand">
        <div className="lp-brand-inner">
          <div className="lp-logo">
            <i className="fas fa-mobile-alt"></i>
          </div>
          <h1 className="lp-brand-title">Device Fleet</h1>
          <p className="lp-brand-sub">
            Manage your entire device inventory from one central place.
          </p>

          <ul className="lp-features">
            {features.map((f) => (
              <li key={f.icon} className="lp-feature-item">
                <span className="lp-feature-icon">
                  <i className={`fas ${f.icon}`}></i>
                </span>
                <span>{f.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* decorative blobs */}
        <div className="lp-blob lp-blob-1" aria-hidden="true"></div>
        <div className="lp-blob lp-blob-2" aria-hidden="true"></div>
      </div>

      {/* ── Right form panel ── */}
      <div className="lp-form-panel">
        <div className="lp-form-card">
          <div className="lp-form-header">
            <div className="lp-form-icon">
              <i className="fas fa-sign-in-alt"></i>
            </div>
            <h2 className="lp-form-title">Welcome back</h2>
            <p className="lp-form-desc">Sign in to access your dashboard</p>
          </div>

          {error && (
            <div className="lp-error" role="alert">
              <i className="fas fa-exclamation-circle"></i>
              <span>{error}</span>
            </div>
          )}

          {serverGoogleEnabled === null && (
            <div className="lp-checking">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Checking sign-in options…</span>
            </div>
          )}

          {canUseGoogle && (
            <div className={`lp-google-wrap ${loading ? 'lp-loading' : ''}`}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError('Google sign-in was cancelled or failed')}
                useOneTap={false}
                theme="outline"
                size="large"
                width="100%"
                text="continue_with"
                shape="rectangular"
                disabled={loading}
              />
            </div>
          )}

          {serverGoogleEnabled !== null && !canUseGoogle && (
            <div className="lp-config-warn">
              <i className="fas fa-exclamation-triangle"></i>
              <p>
                Google sign-in is not available. Set{' '}
                <code>ENABLE_GOOGLE_LOGIN: true</code> and a valid{' '}
                <code>GOOGLE_CLIENT_ID</code> in{' '}
                <code>backend/config.js</code> and{' '}
                <code>frontend/src/config.js</code>, then restart the API.
              </p>
            </div>
          )}

          {canUseGoogle && (
            <p className="lp-footer-note">
              <i className="fas fa-lock"></i>
              Use your organization Google account to sign in.
            </p>
          )}

          <div className="lp-version">
            <span>v{packageJson.version}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const Login = ({ onLoggedIn }) => {
  const wrapGoogle = ENABLE_GOOGLE_LOGIN && !!GOOGLE_CLIENT_ID;

  if (wrapGoogle) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <LoginForm onLoggedIn={onLoggedIn} />
      </GoogleOAuthProvider>
    );
  }

  return (
    <div className="lp-root">
      <div className="lp-brand">
        <div className="lp-brand-inner">
          <div className="lp-logo">
            <i className="fas fa-mobile-alt"></i>
          </div>
          <h1 className="lp-brand-title">Device Fleet</h1>
          <p className="lp-brand-sub">Manage your entire device inventory from one central place.</p>
          <ul className="lp-features">
            {features.map((f) => (
              <li key={f.icon} className="lp-feature-item">
                <span className="lp-feature-icon"><i className={`fas ${f.icon}`}></i></span>
                <span>{f.text}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lp-blob lp-blob-1" aria-hidden="true"></div>
        <div className="lp-blob lp-blob-2" aria-hidden="true"></div>
      </div>

      <div className="lp-form-panel">
        <div className="lp-form-card">
          <div className="lp-form-header">
            <div className="lp-form-icon"><i className="fas fa-cog"></i></div>
            <h2 className="lp-form-title">Setup Required</h2>
            <p className="lp-form-desc">Google sign-in is not configured yet.</p>
          </div>
          <div className="lp-config-warn">
            <i className="fas fa-exclamation-triangle"></i>
            <p>
              Set <code>ENABLE_GOOGLE_LOGIN: true</code> and <code>GOOGLE_CLIENT_ID</code> in{' '}
              <code>frontend/src/config.js</code>.
            </p>
          </div>

          <div className="lp-version">
            <span>v{packageJson.version}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
